import { CommonModule } from '@angular/common';
import { Component, ElementRef, QueryList, ViewChildren, ChangeDetectorRef, inject } from '@angular/core';
import { SidebarService } from '../../shared/services/sidebar.service';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { NgIconComponent } from '@ng-icons/core';
import { combineLatest, Subscription } from 'rxjs';
import { AuthService } from '../../../application/services/auth.service';
import { toNgIconName } from '../../shared/utils/heroicons';

// Tipo interno para cada elemento del menu lateral
type NavItem = {
  name: string;
  icon: string;
  path?: string;
  new?: boolean;
  subItems?: { name: string; path: string; pro?: boolean; new?: boolean }[];
};

// Sidebar de la aplicacion. Muestra los items de navegacion con sus iconos
// y maneja la expansion de submenus con animacion de altura.
// El menu esta hardcodeado por ahora mientras se construyen las paginas.
@Component({
  selector: 'app-sidebar',
  imports: [
    CommonModule,
    RouterModule,
    NgIconComponent,
  ],
  templateUrl: './app-sidebar.component.html',
})
export class AppSidebarComponent {
  navItems: NavItem[] = [];
  othersItems: NavItem[] = [];

  openSubmenu: string | null | number = null;
  subMenuHeights: { [key: string]: number } = {};
  @ViewChildren('subMenu') subMenuRefs!: QueryList<ElementRef>;

  readonly isExpanded$;
  readonly isMobileOpen$;
  readonly isHovered$;

  private subscription: Subscription = new Subscription();
  private authService = inject(AuthService);

  constructor(
    public sidebarService: SidebarService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.isExpanded$ = this.sidebarService.isExpanded$;
    this.isMobileOpen$ = this.sidebarService.isMobileOpen$;
    this.isHovered$ = this.sidebarService.isHovered$;
  }

  ngOnInit() {
    // Carga el menu y suscribe a los eventos de navegacion
    this.loadMenuFromAPI();

    this.subscription.add(
      this.router.events.subscribe(event => {
        if (event instanceof NavigationEnd) {
          this.setActiveMenuFromRoute(this.router.url);
        }
      })
    );

    // Cuando el sidebar se colapsa completamente, fuerza la deteccion de cambios
    this.subscription.add(
      combineLatest([this.isExpanded$, this.isMobileOpen$, this.isHovered$]).subscribe(
        ([isExpanded, isMobileOpen, isHovered]) => {
          if (!isExpanded && !isMobileOpen && !isHovered) {
            this.cdr.detectChanges();
          }
        }
      )
    );

    this.setActiveMenuFromRoute(this.router.url);
  }

  // Construye el menú dinámicamente desde los módulos del API
  private loadMenuFromAPI(): void {
    const modules = this.authService.getModules();

    this.navItems = [
      { icon: this.getIcon('Dashboard'), name: 'Inicio', path: '/home' },
      ...modules.map(mod => {
        if (!mod.showAsList && mod.screens.length === 1) {
          return {
            icon: this.getIcon(mod.icon),
            name: mod.name,
            path: mod.screens[0].path,
          };
        }
        return {
          icon: this.getIcon(mod.icon),
          name: mod.name,
          subItems: mod.screens.map(s => ({ name: s.name, path: s.path })),
        };
      }),
    ];
  }

  private getIcon(key: string): string {
    return toNgIconName(key);
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  isActive(path: string): boolean {
    return this.router.url === path;
  }

  // Abre o cierra el submenu de la seccion indicada con animacion de altura
  toggleSubmenu(section: string, index: number) {
    const key = `${section}-${index}`;

    if (this.openSubmenu === key) {
      this.openSubmenu = null;
      this.subMenuHeights[key] = 0;
    } else {
      this.openSubmenu = key;

      setTimeout(() => {
        const el = document.getElementById(key);
        if (el) {
          this.subMenuHeights[key] = el.scrollHeight;
          this.cdr.detectChanges();
        }
      });
    }
  }

  onSidebarMouseEnter() {
    this.isExpanded$.subscribe(expanded => {
      if (!expanded) {
        this.sidebarService.setHovered(true);
      }
    }).unsubscribe();
  }

  // Cuando se navega a una ruta, abre automaticamente el submenu que la contiene
  private setActiveMenuFromRoute(currentUrl: string) {
    const menuGroups = [
      { items: this.navItems, prefix: 'main' },
      { items: this.othersItems, prefix: 'others' },
    ];

    menuGroups.forEach(group => {
      group.items.forEach((nav, i) => {
        if (nav.subItems) {
          nav.subItems.forEach(subItem => {
            if (currentUrl === subItem.path) {
              const key = `${group.prefix}-${i}`;
              this.openSubmenu = key;

              setTimeout(() => {
                const el = document.getElementById(key);
                if (el) {
                  this.subMenuHeights[key] = el.scrollHeight;
                  this.cdr.detectChanges();
                }
              });
            }
          });
        }
      });
    });
  }

  // En movil, cierra el sidebar al hacer click en un subitem
  onSubmenuClick() {
    console.log('click submenu');
    this.isMobileOpen$.subscribe(isMobile => {
      if (isMobile) {
        this.sidebarService.setMobileOpen(false);
      }
    }).unsubscribe();
  }


}
